import Entity from 'lib/Entity';
import GroupMembership from 'entities/GroupMembership';
import User from 'entities/User';
import { PushEvents } from 'pusher/PusherConstants';
import type { PusherEvent, PusherEventV2, Change, ActionChange, PusherActionV3 } from './types';

const CHANGESET_EVENT_TYPE = 'v3.changeset';

function getModelFromType(actionType: string):
  | {
      new (...args: any): Entity;
    }
  | undefined
  | null {
  switch (actionType) {
    case PushEvents.CLASS_MEMBERSHIP:
      return GroupMembership;
    case PushEvents.USER:
      return User;
    default:
      return null;
  }
}

/*
 * Tries to match the push event data with a model
 * This is how we get entities & collection in v2 push event
 * or v3 content push event
 */
function parseModel(type: string, data: any): any {
  const model = getModelFromType(type);

  if (model) {
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'fromJSON' does not exist on type 'new (.... Remove this comment to see the full error message
    return model.fromJSON(data);
  }

  return undefined;
}

function isV2(event?: PusherEvent): boolean {
  return !!event?.eventData?.data?.v2;
}

function isV3Changeset(event?: PusherEvent): boolean {
  return event?.type === CHANGESET_EVENT_TYPE;
}

export type AggregatedChanges = Record<string, Array<ActionChange<any>>>;

function aggregateChanges(changes: Array<Change<any>>): AggregatedChanges {
  if (!changes) {
    return {};
  }

  return changes.reduce((result, change) => {
    const key = `changeset-${change.action}-${change.type}`;

    if (!result[key]) {
      result[key] = [];
    }

    // We ignore those keys because they are redundant informations
    // We don't need the action & type in the changeset now that
    // we agregated the changes by type & action.
    //
    // {
    //   type: 'changeset-update-group',
    //   eventData: {
    //     changes: [
    //       {uuid: '123-1'},
    //       {uuid: '123-2'},
    //     ]
    //   }
    // }
    const { type, action, ...rest } = change;

    result[key].push({ ...rest });
    return result;
  }, {} as AggregatedChanges);
}

/*
 * V2 push:
 *   - always return a single action (array of 1).
 *   - parse the data & add a model object to the action.
 *
 * V3 changeset push:
 *   - return an array of actions aggregated the model & type.
 *     eg: all the changeset that concerns destroying chat will
 *     be in a { type: changeset-destroy-chat, ... } action.
 */
export function getActions(event: PusherEvent): Array<PusherEventV2 | PusherActionV3> {
  if (isV2(event)) {
    (event as PusherEventV2).model = parseModel(event.type, event.eventData.data.v2);
    return [event];
  }

  const actionsToDispatch: Array<PusherActionV3> = [];

  if (isV3Changeset(event)) {
    const changes = aggregateChanges(event.eventData.changes);
    for (const key in changes) {
      actionsToDispatch.push({
        type: key,
        changes: changes[key],
      });
    }
  }

  return actionsToDispatch;
}
